<template>
  <div class="linkJsonWorkspace" :class="{ 'linkJsonWorkspace--open': visible }">
    <div ref="jsonHeader" class="linkJsonHeader">
      <div
        class="linkJsonDock"
        :class="{ 'linkJsonDock--active': visible }"
      >
        <button type="button" class="linkJsonDock__main" @click="toggleVisible">
          <i class="el-icon-document" aria-hidden="true" />
          <span class="linkJsonDock__text">原始 JSON</span>
          <span v-if="payloadHint" class="linkJsonDock__hint">{{ payloadHint }}</span>
          <span class="linkJsonDock__action">{{ visible ? '收起' : '查看' }}</span>
        </button>
        <div v-if="visible" class="linkJsonDock__tools">
          <button type="button" class="linkJsonDock__toolBtn" @click.stop="handleCopy">
            复制
          </button>
        </div>
      </div>

      <transition name="link-json-sheet">
        <section
          v-if="visible"
          class="linkJsonPanel"
          role="dialog"
          aria-modal="true"
          aria-label="原始 JSON"
          @click.stop
        >
          <div ref="panelBody" class="linkJsonPanel__body">
            <pre ref="jsonPre" class="linkJsonPanel__pre link-font-mono">{{ formattedJson }}</pre>
          </div>
        </section>
      </transition>
    </div>

    <div class="linkJsonWorkspace__content">
      <transition name="link-json-backdrop">
        <div
          v-if="visible"
          class="linkJsonBackdrop"
          aria-hidden="true"
          @click="handleClose"
        />
      </transition>
      <slot />
    </div>
  </div>
</template>

<script>
const JSON_PANEL_MIN_HEIGHT = 120
const JSON_PANEL_MAX_HEIGHT = 860
const JSON_PANEL_MAX_VH = 0.8

/**
 * 通用资源查看页 · 原始 JSON。
 * 抽屉悬浮在正文之上（不顶开布局）；高度随 jsonConfig 收缩，超出上限时在抽屉内滚动。
 * z-index 仅在展开时启用，避免收起态页面滚动时盖住上方 sticky 顶栏。
 */
export default {
  name: 'GenericResourceJsonTopSheet',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    payload: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    formattedJson() {
      try {
        return JSON.stringify(this.payload || {}, null, 2)
      } catch (e) {
        return '{}'
      }
    },
    payloadHint() {
      const keys = Object.keys(this.payload || {})
      if (!keys.length) {
        return ''
      }
      return `${keys.length} 个顶层字段`
    },
  },
  created() {
    this.onResize = () => {
      if (this._resizeSyncTimer) {
        clearTimeout(this._resizeSyncTimer)
      }
      this._resizeSyncTimer = window.setTimeout(() => {
        this.syncPanelHeight()
      }, 120)
    }
  },
  watch: {
    visible(open) {
      if (open) {
        document.addEventListener('keydown', this.handleKeydown)
        this.$nextTick(() => {
          this.syncPanelHeight()
        })
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
        this.resetPanelHeight()
      }
    },
    formattedJson() {
      if (this.visible) {
        this.$nextTick(() => {
          this.syncPanelHeight()
        })
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    if (this._resizeSyncTimer) {
      clearTimeout(this._resizeSyncTimer)
    }
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    resetPanelHeight() {
      const body = this.$refs.panelBody
      const panel = body && body.parentElement
      if (body) {
        body.style.maxHeight = ''
        body.style.overflowY = ''
      }
      if (panel) {
        panel.style.height = ''
      }
    },
    syncPanelHeight() {
      if (!this.visible) {
        return
      }
      const body = this.$refs.panelBody
      if (!body) {
        return
      }
      const panel = body.parentElement
      const viewportCap = Math.floor(window.innerHeight * JSON_PANEL_MAX_VH)
      const hardCap = Math.min(JSON_PANEL_MAX_HEIGHT, viewportCap)

      body.style.maxHeight = 'none'
      body.style.overflowY = 'visible'
      const naturalHeight = body.scrollHeight
      const capped = Math.max(JSON_PANEL_MIN_HEIGHT, Math.min(hardCap, naturalHeight))
      const needsScroll = naturalHeight > hardCap

      body.style.maxHeight = needsScroll ? `${hardCap}px` : `${capped}px`
      body.style.overflowY = needsScroll ? 'auto' : 'hidden'
      if (panel) {
        panel.style.height = needsScroll ? `${hardCap}px` : `${capped}px`
      }
    },
    toggleVisible() {
      this.$emit('update:visible', !this.visible)
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    handleKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.handleClose()
      }
    },
    handleCopy() {
      const text = this.formattedJson
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('已复制到剪贴板')
        }).catch(() => {
          this.fallbackCopy(text)
        })
        return
      }
      this.fallbackCopy(text)
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('已复制到剪贴板')
      } catch (e) {
        this.$message.error('复制失败，请手动选择复制')
      }
      document.body.removeChild(textarea)
    },
  },
}
</script>

<style scoped>
@import './linkListTheme.css';
@import './linkTypography.css';

.linkJsonWorkspace {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 0;
}

.linkJsonHeader {
  position: relative;
  flex-shrink: 0;
}

/* 仅展开时抬升 stacking，避免收起态滚动时盖住上方 sticky 顶栏 */
.linkJsonWorkspace--open .linkJsonHeader {
  z-index: 35;
}

.linkJsonDock {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin-bottom: 12px;
}

.linkJsonDock--active {
  margin-bottom: 0;
}

.linkJsonDock__main {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 8px;
  background: var(--link-surface-muted, #f7f4ef);
  font-size: var(--link-text-sm);
  color: var(--link-ink);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease,
    border-radius 0.15s ease;
}

.linkJsonDock__main:hover {
  border-color: var(--link-accent-border, #cce5ff);
  background: var(--link-accent-bg, #f0f7ff);
}

.linkJsonDock--active .linkJsonDock__main {
  border-color: var(--link-line-strong, #e5dfd6);
  background: var(--link-surface-head, #f3f0ea);
  box-shadow: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
}

.linkJsonDock__text {
  flex: 0 0 auto;
  font-weight: var(--link-font-semibold);
}

.linkJsonDock__hint {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-regular);
  color: var(--link-ink-muted);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linkJsonDock__action {
  flex-shrink: 0;
  font-size: var(--link-text-xs);
  color: var(--link-accent, #2563eb);
  font-weight: var(--link-font-semibold);
}

.linkJsonDock__tools {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.linkJsonDock__toolBtn {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 8px;
  background: var(--link-card-surface, #f7f4ef);
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.linkJsonDock__toolBtn:hover {
  background: var(--link-surface-muted, #f0ebe3);
  border-color: var(--link-ink-faint);
}

/* 抽屉紧贴 dock 底边，与 main 按钮下圆角移除后连体 */
.linkJsonPanel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
  background: var(--link-json-panel-solid, var(--link-card-surface, #f7f4ef));
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-top: none;
  border-radius: 0 0 var(--link-radius-md, 12px) var(--link-radius-md, 12px);
  box-shadow: var(--link-shadow-card, 0 10px 32px rgba(28, 25, 23, 0.08));
}

.linkJsonPanel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  padding: 12px 14px 14px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: var(--link-json-panel-solid, var(--link-card-surface, #f7f4ef));
}

.linkJsonPanel__pre {
  margin: 0;
  padding: 14px 16px 16px;
  border-radius: var(--link-radius-sm, 8px);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  background: var(--link-json-well-solid, #ffffff);
  color: var(--link-json-code-ink, var(--link-ink-secondary, #44403c));
  font-size: var(--link-text-sm);
  line-height: 1.65;
  white-space: pre;
  word-break: normal;
  tab-size: 2;
}

.linkJsonBackdrop {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: var(--link-json-backdrop, rgba(28, 25, 23, 0.32));
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.linkJsonWorkspace__content {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}

.linkJsonWorkspace--open .linkJsonWorkspace__content {
  z-index: 1;
}

.link-json-backdrop-enter-active,
.link-json-backdrop-leave-active {
  transition: opacity 0.22s ease;
}

.link-json-backdrop-enter,
.link-json-backdrop-leave-to {
  opacity: 0;
}

.link-json-sheet-enter-active,
.link-json-sheet-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.22s ease;
}

.link-json-sheet-enter,
.link-json-sheet-leave-to {
  transform: translateY(6px);
  opacity: 0;
}

.link-json-sheet-enter-to,
.link-json-sheet-leave {
  transform: translateY(0);
  opacity: 1;
}

@media (max-width: 640px) {
  .linkJsonDock {
    flex-direction: column;
    align-items: stretch;
  }

  .linkJsonDock__tools {
    justify-content: flex-end;
  }

  .linkJsonPanel__pre {
    padding: 12px 14px 16px;
    font-size: var(--link-text-xs);
  }
}
</style>
